import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCampusAuth } from "@/contexts/CampusAuth";
import { Upload, Users, FileText, CheckCircle2, AlertTriangle, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE_CSV = `email,name,tier,stripe_customer_id,organisation
maria.schmidt@example.de,Maria Schmidt,Single-Held,,
team@pflegedienst-x.de,Pflegedienst X,Hauptquartier,cus_abc123,Pflegedienst X GmbH
admin.test@example.de,Admin Test,Helden-Basic,,
`;

export default function AdminUserImport() {
  const { user } = useCampusAuth();
  const [csvText, setCsvText] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [imported, setImported] = useState(null);

  const loadImported = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/users/imported?limit=100`, { withCredentials: true });
      setImported(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (user?.is_admin) loadImported(); }, [user, loadImported]);

  if (!user?.is_admin) {
    return (
      <div data-testid="user-import-locked" className="rounded-3xl border border-cream-300 bg-white p-8">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred mb-2">Admin only</div>
        <p className="text-sand-700">Diese Funktion ist Admins vorbehalten.</p>
      </div>
    );
  }

  const submit = async () => {
    if (!csvText.trim() || csvText.trim().length < 10) {
      toast.error("Bitte CSV-Text einfügen (Header + mindestens 1 Zeile).");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/users/import-csv`,
        { csv_text: csvText, dry_run: dryRun },
        { withCredentials: true });
      setResult(r.data);
      const c = r.data.counts;
      toast.success(
        dryRun
          ? `Dry-Run: ${c.created} würden angelegt, ${c.skipped} schon vorhanden, ${c.error} Fehler.`
          : `Import erledigt: ${c.created} angelegt, ${c.skipped} geskippt, ${c.error} Fehler.`
      );
      if (!dryRun) await loadImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const copyTempPasswords = () => {
    if (!result?.results) return;
    const lines = result.results
      .filter((r) => r.temp_password)
      .map((r) => `${r.email}\t${r.temp_password}`);
    if (!lines.length) {
      toast.info("Keine Temp-Passwörter im aktuellen Result.");
      return;
    }
    const text = "email\ttemp_password\n" + lines.join("\n");
    navigator.clipboard.writeText(text);
    toast.success(`${lines.length} Zeilen kopiert.`);
  };

  return (
    <div className="space-y-6" data-testid="admin-user-import">
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-clay-dark">
          Admin · Bestandskunden-Importer
        </div>
        <h1 className="font-display text-3xl md:text-4xl text-sand-800 mt-1">
          User & Stripe-Kunden in Bulk anlegen
        </h1>
        <p className="text-sand-600 text-[14.5px] mt-2 max-w-3xl">
          CSV einfügen → Spalten: <code className="bg-cream-100 px-1.5 py-0.5 rounded text-[12px]">email,name,tier,password,stripe_customer_id,organisation,is_admin</code>.
          Akzeptiert auch Stripe-Customer-Export-CSVs (erkennt „Email" + „Name" automatisch).
        </p>
      </div>

      <div className="rounded-3xl border border-cream-300 bg-white p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-clay-dark" strokeWidth={2} />
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
              CSV einfügen
            </div>
          </div>
          <Button onClick={() => setCsvText(SAMPLE_CSV)} variant="outline" size="sm"
            data-testid="admin-import-sample"
            className="h-9 rounded-full border-clay text-[12.5px]">
            <Copy className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} />
            Beispiel-CSV einfügen
          </Button>
        </div>

        <textarea
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          placeholder="email,name,tier&#10;maria@example.de,Maria,Single-Held"
          rows={10}
          data-testid="admin-import-csv-text"
          className="w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[13px] font-mono text-sand-800 focus:outline-none focus:border-terra"
        />

        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)}
              data-testid="admin-import-dry-run"
              className="h-4 w-4 accent-terra" />
            <span className="text-[13px] text-sand-700">
              <strong>Dry-Run</strong> · zeigt nur was passieren würde (keine echten Inserts)
            </span>
          </label>
          <Button onClick={submit} disabled={busy || !csvText.trim()}
            data-testid="admin-import-submit"
            className="h-11 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 px-6 font-semibold">
            {busy ? "Verarbeite …" : dryRun ? "Dry-Run starten" : "Import jetzt durchführen"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="rounded-3xl border border-cream-300 bg-cream-50 p-6"
          data-testid="admin-import-result">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
              {result.dry_run ? "Dry-Run-Ergebnis" : "Import-Ergebnis"}
            </div>
            <div className="flex gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 px-3 h-7 rounded-full bg-sage-dark/15 text-sage-dark text-[11.5px] font-bold">
                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
                {result.counts.created} {result.dry_run ? "würden" : ""} angelegt
              </span>
              <span className="inline-flex items-center px-3 h-7 rounded-full bg-saffron/25 text-clay-dark text-[11.5px] font-bold">
                {result.counts.skipped} geskippt
              </span>
              {result.counts.error > 0 && (
                <span className="inline-flex items-center gap-1 px-3 h-7 rounded-full bg-brand-darkred/15 text-brand-darkred text-[11.5px] font-bold">
                  <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} />
                  {result.counts.error} Fehler
                </span>
              )}
              {!result.dry_run && result.results?.some((r) => r.temp_password) && (
                <Button onClick={copyTempPasswords} size="sm" variant="outline"
                  data-testid="admin-import-copy-passwords"
                  className="h-7 rounded-full border-clay text-[11.5px]">
                  <Copy className="h-3 w-3 mr-1" strokeWidth={2} />
                  Temp-Passwörter kopieren
                </Button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-sand-500 text-[11px] uppercase tracking-[0.18em]">
                  <th className="py-1.5 pr-3">E-Mail</th>
                  <th className="py-1.5 pr-3">Status</th>
                  <th className="py-1.5 pr-3">Tier</th>
                  <th className="py-1.5 pr-3">Temp-Passwort</th>
                  <th className="py-1.5 pr-3">Notiz</th>
                </tr>
              </thead>
              <tbody>
                {result.results?.map((r, i) => (
                  <tr key={i} className="border-t border-cream-200"
                    data-testid={`admin-import-row-${r.email.replace(/[@.]/g, "_")}`}>
                    <td className="py-1.5 pr-3 font-mono text-[12px]">{r.email}</td>
                    <td className="py-1.5 pr-3">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="py-1.5 pr-3">{r.tier || "—"}</td>
                    <td className="py-1.5 pr-3 font-mono text-[11.5px]">
                      {r.temp_password ? <span className="bg-saffron/30 px-1.5 py-0.5 rounded">{r.temp_password}</span> : "—"}
                    </td>
                    <td className="py-1.5 pr-3 text-sand-500 text-[11.5px]">
                      {r.reason || r.note || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-3xl border border-cream-300 bg-white p-6"
        data-testid="admin-import-imported-list">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-5 w-5 text-terra" strokeWidth={2} />
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-terra">
              Bereits via Importer angelegt
            </div>
            <div className="text-[12px] text-sand-500">
              {imported?.count || 0} Importe insgesamt
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-sand-500 text-[11px] uppercase tracking-[0.18em]">
                <th className="py-1.5 pr-3">E-Mail</th>
                <th className="py-1.5 pr-3">Name</th>
                <th className="py-1.5 pr-3">Tier</th>
                <th className="py-1.5 pr-3">Stripe-Customer</th>
                <th className="py-1.5 pr-3">Verified</th>
                <th className="py-1.5 pr-3">Import-Datum</th>
              </tr>
            </thead>
            <tbody>
              {imported?.users?.length ? imported.users.map((u, i) => (
                <tr key={i} className="border-t border-cream-200">
                  <td className="py-1.5 pr-3 font-mono text-[12px]">{u.email}</td>
                  <td className="py-1.5 pr-3">{u.name}</td>
                  <td className="py-1.5 pr-3">{u.tier}</td>
                  <td className="py-1.5 pr-3 font-mono text-[11.5px] text-sand-500">{u.stripe_customer_id || "—"}</td>
                  <td className="py-1.5 pr-3">{u.email_verified ? "ja" : "nein"}</td>
                  <td className="py-1.5 pr-3 text-[11.5px] text-sand-500">{u.imported_at?.slice(0, 16).replace("T", " ")}</td>
                </tr>
              )) : (
                <tr><td colSpan={6} className="py-5 text-center text-sand-500 italic">
                  Noch keine User via Importer angelegt.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    "created": "bg-sage-dark text-cream-50",
    "would-create": "bg-sage-dark/20 text-sage-dark",
    "skipped": "bg-saffron/25 text-clay-dark",
    "error": "bg-brand-darkred text-cream-50",
  };
  return (
    <span className={`inline-flex items-center px-2 h-6 rounded-full text-[10.5px] uppercase tracking-[0.15em] font-bold ${map[status] || "bg-cream-200"}`}>
      {status}
    </span>
  );
}
